import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { t } from 'i18next'
import { useDispatch } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons'
import DynamicTable from 'Components/DynamicTable/DynamicTable'
import MiniLoading from 'Components/MiniLoading'
import CustomSnackbar from 'Components/CustomSnackbar'
import { useDebounce } from 'Hooks/useDebounce'
import QuickListFilters from './components/QuickListFilters'
import DeleteQuickListModal from './components/DeleteQuickListModal'
import ShowProductsModal from './components/ShowProductsModal'
import useColumnSearchQuickList from './hooks/useColumnSearchQuickList'
import {
  QUICK_LIST_PAGE_SIZE_OPTIONS,
  QUICK_LIST_ROUTES,
} from './constants/quickList.constants'
import {
  buildQuickListSearchPayload,
  extractQuickListItems,
  extractQuickListTotal,
} from './utils/quickList.utils'
import {
  searchQuickListSettingsAction,
  getQuickListSettingAction,
  deleteQuickListSettingAction,
} from 'redux/actions/quickListSettingsActions'
import './SearchQuickList.css'

const WINDOW_SIZE = 3

const SearchQuickList = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const debounce = useDebounce()

  const [items, setItems] = useState([])
  const [searchText, setSearchText] = useState('')
  const [debouncedSearchText, setDebouncedSearchText] = useState('')
  const [status, setStatus] = useState(true)
  const [loadingData, setLoadingData] = useState(false)

  const [previewItem, setPreviewItem] = useState(null)
  const [loadingPreviewProducts, setLoadingPreviewProducts] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [showModalDeleteQuickList, setShowModalDeleteQuickList] =
    useState(false)

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
  })
  const [knownLastPage, setKnownLastPage] = useState(1)
  const [pageWindowFirst, setPageWindowFirst] = useState(0)
  const [pageWindowLast, setPageWindowLast] = useState(WINDOW_SIZE)
  const [searchToken, setSearchToken] = useState(0)

  const [snackbarMessage, setSnackbarMessage] = useState({
    open: false,
    message: '',
    severity: 'warning',
  })

  const searchRequestIdRef = useRef(0)
  const previewRequestIdRef = useRef(0)

  const pageNumbers = Array.from({ length: knownLastPage }, (_, i) => i + 1)

  const resetPagination = useCallback(() => {
    setPagination(prev => ({ ...prev, currentPage: 1 }))
    setKnownLastPage(1)
    setPageWindowFirst(0)
    setPageWindowLast(WINDOW_SIZE)
    // Forces searchQuickLists to re-run even when the currentPage/pageSize/status/
    // debouncedSearchText all end up unchanged (e.g., clearing filters while
    // already on the default page 1 view) — otherwise knownLastPage stays
    // stuck at the 1 set above and later pages disappear.
    setSearchToken(prev => prev + 1)
  }, [])

  const goToLastValidPage = useCallback(lastValidPage => {
    const safePage = Math.max(1, lastValidPage)
    setPagination(prev => ({ ...prev, currentPage: safePage }))
    setKnownLastPage(safePage)
    setPageWindowFirst(prev => Math.min(prev, Math.max(0, safePage - 1)))
    setPageWindowLast(safePage)
  }, [])

  const searchQuickLists = useCallback(async () => {
    const requestId = ++searchRequestIdRef.current
    setLoadingData(true)

    try {
      const payload = buildQuickListSearchPayload({
        pagination,
        status,
        searchText: debouncedSearchText,
      })
      const data = await dispatch(searchQuickListSettingsAction(payload))

      if (requestId !== searchRequestIdRef.current) return

      const pageItems = extractQuickListItems(data)
      const total = extractQuickListTotal(data)

      if (pageItems.length === 0 && pagination.currentPage > 1) {
        if (total !== null) {
          goToLastValidPage(Math.max(1, Math.ceil(total / pagination.pageSize)))
        } else {
          goToLastValidPage(pagination.currentPage - 1)
        }
        return
      }

      setItems(pageItems)

      if (total !== null) {
        setKnownLastPage(Math.max(1, Math.ceil(total / pagination.pageSize)))
      } else if (pageItems.length === pagination.pageSize) {
        setKnownLastPage(prev => Math.max(prev, pagination.currentPage + 1))
      } else {
        setKnownLastPage(pagination.currentPage)
      }
    } catch (error) {
      if (requestId !== searchRequestIdRef.current) return
      setItems([])
      setSnackbarMessage({
        open: true,
        message: t('QUICK_LIST_SETTINGS.MESSAGE.FAIL_LOAD'),
        severity: 'error',
      })
    } finally {
      if (requestId === searchRequestIdRef.current) setLoadingData(false)
    }
  }, [
    pagination.currentPage,
    pagination.pageSize,
    status,
    debouncedSearchText,
    searchToken,
    dispatch,
    goToLastValidPage,
  ])

  useEffect(() => {
    searchQuickLists()
  }, [searchQuickLists])

  const handleClose = () => {
    setSnackbarMessage(prev => ({ ...prev, open: false }))
  }

  const handleClearFilters = () => {
    setSearchText('')
    setDebouncedSearchText('')
    setStatus(true)
    resetPagination()
  }

  const handleSearchTextChange = value => {
    setSearchText(value)
    resetPagination()
    debounce(() => setDebouncedSearchText(value), 500)
  }

  const handlePageSizeChange = event => {
    const nextPageSize = Number(event.target.value)
    resetPagination()
    setPagination(prev => ({
      ...prev,
      pageSize: nextPageSize,
    }))
  }

  const handlePrevPage = () => {
    if (pagination.currentPage <= 1) return
    setPageWindowFirst(prev => Math.max(0, prev - 1))
    setPageWindowLast(prev => Math.max(WINDOW_SIZE, prev - 1))
    setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))
  }

  const handleNextPage = () => {
    if (pagination.currentPage >= knownLastPage) return
    setPageWindowFirst(prev =>
      Math.max(0, Math.min(pageNumbers.length - WINDOW_SIZE, prev + 1))
    )
    setPageWindowLast(prev => Math.min(pageNumbers.length, prev + 1))
    setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))
  }

  const handlePageClick = e => {
    const page = parseInt(e.target.value)
    setPageWindowFirst(page - 1)
    setPageWindowLast(page - 1 + WINDOW_SIZE)
    setPagination(prev => ({ ...prev, currentPage: page }))
  }

  const handleReturnFirst = () => {
    setPageWindowFirst(0)
    setPageWindowLast(WINDOW_SIZE)
    setPagination(prev => ({ ...prev, currentPage: 1 }))
  }

  const handleJumpForward = () => {
    const newFirst = pageWindowLast
    const newLast = Math.min(pageNumbers.length, pageWindowLast + WINDOW_SIZE)
    setPageWindowFirst(newFirst)
    setPageWindowLast(newLast)
    setPagination(prev => ({ ...prev, currentPage: pageWindowLast + 1 }))
  }

  const handleViewProducts = async item => {
    const requestId = ++previewRequestIdRef.current
    setPreviewItem(item)
    setLoadingPreviewProducts(true)

    try {
      const detail = await dispatch(
        getQuickListSettingAction(item.ProductCustomListId)
      )

      if (requestId !== previewRequestIdRef.current) return

      setPreviewItem(prev =>
        prev?.ProductCustomListId === item.ProductCustomListId
          ? { ...prev, ...detail }
          : prev
      )
      setLoadingPreviewProducts(false)
    } catch (error) {
      if (requestId !== previewRequestIdRef.current) return
      setSnackbarMessage({
        open: true,
        message: t('QUICK_LIST_SETTINGS.MESSAGE.FAIL_LOAD'),
        severity: 'error',
      })
      setLoadingPreviewProducts(false)
    }
  }

  const handleDelete = item => {
    setDeleteItem(item)
    setShowModalDeleteQuickList(true)
  }

  const handleConfirmDeleteQuickList = async () => {
    if (!deleteItem) return

    try {
      const success = await dispatch(
        deleteQuickListSettingAction(deleteItem.ProductCustomListId)
      )

      if (success) {
        setSnackbarMessage({
          open: true,
          message: t('QUICK_LIST_SETTINGS.MESSAGE.SUCCESS_DELETE'),
          severity: 'success',
        })
        setDeleteItem(null)
        searchQuickLists()
      } else {
        setSnackbarMessage({
          open: true,
          message: t('QUICK_LIST_SETTINGS.MESSAGE.FAIL_DELETE'),
          severity: 'error',
        })
      }
    } catch (error) {
      setSnackbarMessage({
        open: true,
        message: t('QUICK_LIST_SETTINGS.MESSAGE.FAIL_DELETE'),
        severity: 'error',
      })
    } finally {
      setShowModalDeleteQuickList(false)
    }
  }

  const columns = useColumnSearchQuickList({
    onViewProducts: handleViewProducts,
    onEdit: item =>
      history.push(QUICK_LIST_ROUTES.EDIT(item.ProductCustomListId)),
    onDelete: handleDelete,
    pagination,
  })

  return (
    <>
      <div className="search-quick-list" data-testid="container-quick-list">
        <QuickListFilters
          searchText={searchText}
          status={status}
          loading={loadingData}
          onSearchTextChange={handleSearchTextChange}
          onStatusChange={newStatus => {
            setStatus(newStatus)
            resetPagination()
          }}
          onClearFilters={handleClearFilters}
          onCreate={() => history.push(QUICK_LIST_ROUTES.CREATE)}
        />

        {loadingData ? (
          <MiniLoading />
        ) : (
          <DynamicTable
            columns={columns}
            data={items}
            loadingData={loadingData}
          />
        )}

        <div
          className="search-quick-list-footer"
          data-testid="container-pagination">
          <div className="footer-content">
            <p>
              {t('QUICK_LIST_SETTINGS.LIST.SHOWING_PAGE', {
                currentPage: pagination.currentPage,
              })}
            </p>

            <select
              data-testid="input-page-size"
              className="page-size-selector"
              onChange={handlePageSizeChange}
              value={pagination.pageSize}>
              {QUICK_LIST_PAGE_SIZE_OPTIONS.map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          <div className="quick-list-pagination">
            <button
              className="btn-quick-list-prev-page"
              data-testid="btn-prev-page"
              disabled={pagination.currentPage <= 1 || loadingData}
              onClick={handlePrevPage}>
              <FontAwesomeIcon
                icon={faChevronLeft}
                style={{ fontSize: '1.8rem' }}
              />
            </button>

            {pageWindowFirst > 0 && (
              <button
                className="btn-quick-list-regular-page"
                onClick={handleReturnFirst}>
                ...
              </button>
            )}

            {pageNumbers.slice(pageWindowFirst, pageWindowLast).map(pageNum => (
              <button
                data-testid={`btn-page-${pageNum}`}
                key={pageNum}
                className={
                  pagination.currentPage === pageNum
                    ? 'btn-quick-list-active-page'
                    : 'btn-quick-list-regular-page'
                }
                value={pageNum}
                onClick={handlePageClick}
                disabled={loadingData}>
                {pageNum}
              </button>
            ))}

            {pageWindowLast < pageNumbers.length && (
              <button
                className="btn-quick-list-regular-page"
                onClick={handleJumpForward}>
                ...
              </button>
            )}

            <button
              className="btn-quick-list-next-page"
              data-testid="btn-next-page"
              disabled={pagination.currentPage >= knownLastPage || loadingData}
              onClick={handleNextPage}>
              <FontAwesomeIcon
                icon={faChevronRight}
                style={{ fontSize: '1.8rem' }}
              />
            </button>
          </div>
        </div>

        <ShowProductsModal
          item={previewItem}
          loading={loadingPreviewProducts}
          onClose={() => setPreviewItem(null)}
        />
        <DeleteQuickListModal
          showModalDeleteQuickList={showModalDeleteQuickList}
          setShowModalDeleteQuickList={setShowModalDeleteQuickList}
          handleConfirmDeleteQuickList={handleConfirmDeleteQuickList}
        />
      </div>
      <CustomSnackbar
        open={snackbarMessage.open}
        message={snackbarMessage.message}
        severity={snackbarMessage.severity}
        onClose={handleClose}
      />
    </>
  )
}

export default SearchQuickList
